import {
  Timestamp,
  type DocumentData,
  type DocumentSnapshot,
  type QueryDocumentSnapshot,
} from 'firebase/firestore'
import type { Webinar } from '@domain/models/webinar'

function toOptionalDate(value: unknown): Date | undefined {
  if (value instanceof Timestamp) {
    return value.toDate()
  }
  if (value instanceof Date) {
    return value
  }
  return undefined
}

function stripNullishDeep<T>(value: T): T {
  if (Array.isArray(value)) {
    const cleaned = value
      .map((item) => stripNullishDeep(item))
      .filter((item) => item !== null && item !== undefined)
    return cleaned as T
  }

  if (!value || typeof value !== 'object') {
    return value
  }

  if (value instanceof Date || value instanceof Timestamp) {
    return value
  }

  const output: Record<string, unknown> = {}
  for (const [key, nestedValue] of Object.entries(value as Record<string, unknown>)) {
    const cleaned = stripNullishDeep(nestedValue)
    if (cleaned !== null && cleaned !== undefined) {
      output[key] = cleaned
    }
  }

  return output as T
}

export function fromFirestoreWebinar(
  docSnap: DocumentSnapshot<DocumentData> | QueryDocumentSnapshot<DocumentData>,
): Webinar {
  const data = (docSnap.data() ?? {}) as Record<string, unknown>
  const webinarDate = toOptionalDate(data['webinar_date']) ?? new Date(0)

  return {
    uid: docSnap.id,
    title: typeof data['title'] === 'string' ? data['title'] : '',
    webinarDate,
    webinarTime:
      typeof data['webinar_time'] === 'string' ? data['webinar_time'] : undefined,
    timeZone: typeof data['time_zone'] === 'string' ? data['time_zone'] : undefined,
    durationMinutes:
      typeof data['duration_minutes'] === 'number'
        ? data['duration_minutes']
        : undefined,
    youtubeUrl:
      typeof data['youtube_url'] === 'string' ? data['youtube_url'] : undefined,
    coverImageUrl:
      typeof data['cover_image_url'] === 'string'
        ? data['cover_image_url']
        : undefined,
    webinarCategoryId:
      typeof data['webinar_category_id'] === 'string'
        ? data['webinar_category_id']
        : undefined,
    summary: typeof data['summary'] === 'string' ? data['summary'] : undefined,
    registerUrl:
      typeof data['register_url'] === 'string' ? data['register_url'] : undefined,
    createdAt: toOptionalDate(data['created_at']),
  }
}

export function toFirestoreWebinarPatch(
  input: Partial<Webinar>,
): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    title: input.title,
    webinar_date: input.webinarDate
      ? Timestamp.fromDate(input.webinarDate)
      : undefined,
    webinar_time: input.webinarTime,
    time_zone: input.timeZone,
    duration_minutes: input.durationMinutes,
    youtube_url: input.youtubeUrl,
    cover_image_url: input.coverImageUrl,
    webinar_category_id: input.webinarCategoryId,
    summary: input.summary,
    register_url: input.registerUrl,
    created_at: input.createdAt ? Timestamp.fromDate(input.createdAt) : undefined,
  }

  return stripNullishDeep(payload)
}
